import { useState, useMemo, useEffect } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { LocationOn } from "@mui/icons-material";
import Grid from "@mui/material/Grid";
import { debounce } from "@mui/material/utils";

import "./autocomplete.css";
import DashboardApi from "../utils/api/dashboardApi";

export function GeoAutoComplete(props) {
  const [value, setValue] = useState(null);
  const [isDisabled, setIsDisabled] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const { triggerNextStep } = props;

  const fetcher = useMemo(
    () =>
      debounce(async (request, callback) => {
        try {
          const response = await DashboardApi.post("/v1/servman/geomap", {
            queryTerm: request.input,
          });

          if (response.status === 200) {
            callback(response.data);
          } else {
            console.log("No matching suggestions found");
          }
        } catch (error) {
          console.log("HERE Maps API error:", error);
        }
      }, 400),
    [],
  );

  // Helper function to get unique options by address label
  const getUniqueOptions = (currentValue, results) => {
    const uniqueMap = new Map();

    // Add the current value if it exists
    if (currentValue) {
      uniqueMap.set(currentValue.address.label, currentValue);
    }

    // Add results without duplicating
    if (results?.items) {
      // biome-ignore lint/complexity/noForEach: <explanation>
      results.items.forEach((item) => {
        uniqueMap.set(item.address.label, item);
      });
    }

    // Convert back to array
    return Array.from(uniqueMap.values());
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    let active = true;

    if (inputValue === "") {
      setOptions(value ? [value] : []);
      return undefined;
    }

    fetcher({ input: inputValue }, (results) => {
      if (active && results) {
        // Use the helper function to get unique options
        const uniqueOptions = getUniqueOptions(value, results);
        setOptions(uniqueOptions);
      }
    });

    return () => {
      active = false;
    };
  }, [value, inputValue, fetcher]);

  const showUnifiedHint = inputValue.trim() === "";

  return (
    <Autocomplete
      className="geo-autocomplete"
      id="sb-auto-complete"
      getOptionLabel={(option) =>
        typeof option === "string" ? option : option.address.label
      }
      filterOptions={(x) => x}
      options={options}
      autoComplete
      fullWidth
      disabled={isDisabled}
      includeInputInList
      filterSelectedOptions
      value={value}
      noOptionsText="No matches found"
      isOptionEqualToValue={(option, value) => {
        return option.address?.label === value?.address?.label;
      }}
      onChange={(event, newValue) => {
        if (!newValue) {
          setValue(null);
          setOptions([]);
          return;
        }
        setOptions(newValue ? [newValue] : options);
        setValue(newValue);

        setIsDisabled(true);
        triggerNextStep({
          value: newValue.address,
          sendUserMessage: true,
          displayMessage: newValue.address.label,
        });
      }}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      renderInput={(params) => (
        <Box className="geo-autocomplete-input-wrap" sx={{ position: "relative", width: "100%" }}>
          <TextField
            {...params}
            fullWidth
            placeholder=""
            inputProps={{
              ...params.inputProps,
              "aria-label": "Search address",
            }}
            sx={{
              width: "98%",
              maxWidth: "98%",
              ml: { xs: 0, sm: 1.25 },
              "& .MuiOutlinedInput-root": {
                borderRadius: 1,
                borderStyle: "solid",
                backgroundColor: "white",
                minHeight: { xs: 48, sm: 44 },                
              },
              "& .MuiInputBase-input": {
                color: "#0E283FD9",
                fontSize: 16,
                py: { xs: 1.5, sm: 1.25 },
              },
            }}
          />
          {showUnifiedHint ? (
            <span className="geo-autocomplete-unified-hint" aria-hidden="true">
              <FontAwesomeIcon icon={faSearch} className="geo-autocomplete-unified-hint__icon" />
              <span className="geo-autocomplete-unified-hint__text">Search address</span>
            </span>
          ) : null}
        </Box>
      )}
      renderOption={(props, option) => {
        // Use address label as key instead of random ID for better performance
        return (
          <li {...props} key={option.address.label}>
            <Grid container alignItems="center" wrap="nowrap">
              <Grid item sx={{ display: "flex", width: 44, flexShrink: 0 }}>
                <LocationOn sx={{ color: "text.secondary" }} />
              </Grid>
              <Grid
                item
                sx={{
                  width: "calc(100% - 44px)",
                  minWidth: 0,
                  wordWrap: "break-word",
                  overflowWrap: "break-word",
                }}
              >
                <Box component="span" sx={{ fontWeight: "regular" }}>
                  {option.address.label}
                </Box>
              </Grid>
            </Grid>
          </li>
        );
      }}
    />
  );
}
